/**
 * What the device's WebGL can do (lifted from the shared prototype engine's
 * `engine/src/graphics-support.js`): WebGL2 alone does not guarantee a
 * renderable half-float colour buffer. `mediumSupported` is the capability
 * check `apps/web` uses to pick the default tier: Medium when WebGL2 renders
 * to a half-float colour attachment and to a depth texture, else Off. It
 * probes a throwaway canvas and releases its context. The GPU timer query
 * lives in `gpu-timer.ts`.
 */

type Probe = { readonly webgl2: boolean; readonly halfFloatColor: boolean; readonly depthTexture: boolean };

/** Whether a framebuffer with this texture attached is complete (restores the bindings it touched). */
const renderable = (gl: WebGL2RenderingContext, attach: (texture: WebGLTexture) => void): boolean => {
  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();
  try {
    if (texture === null || framebuffer === null) return false;
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    attach(texture);
    return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
  } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.deleteFramebuffer(framebuffer);
    gl.deleteTexture(texture);
  }
};

/** Probes WebGL2 on a throwaway canvas: half-float colour attachments and depth textures. */
export const inspectGraphics = (doc: Document): Probe => {
  const gl = doc.createElement('canvas').getContext('webgl2');
  if (gl === null) return { webgl2: false, halfFloatColor: false, depthTexture: false };
  try {
    const float = gl.getExtension('EXT_color_buffer_float') !== null || gl.getExtension('EXT_color_buffer_half_float') !== null;
    const halfFloatColor =
      float &&
      renderable(gl, (t) => {
        gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA16F, 2, 2);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
      });
    const depthTexture = renderable(gl, (t) => {
      gl.texStorage2D(gl.TEXTURE_2D, 1, gl.DEPTH_COMPONENT24, 2, 2);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, t, 0);
    });
    return { webgl2: true, halfFloatColor, depthTexture };
  } finally {
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  }
};

/** The default tier for this device: Medium with WebGL2, half-float colour buffers and depth textures; else Off. */
export const defaultTier = (doc: Document): 'off' | 'medium' => {
  const g = inspectGraphics(doc);
  return g.webgl2 && g.halfFloatColor && g.depthTexture ? 'medium' : 'off';
};

/** Whether the renderer's own context renders to half-float colour (the lens target's type). */
export const halfFloatTarget = (gl: WebGL2RenderingContext | WebGLRenderingContext): boolean =>
  gl instanceof WebGL2RenderingContext &&
  (gl.getExtension('EXT_color_buffer_float') !== null || gl.getExtension('EXT_color_buffer_half_float') !== null) &&
  renderable(gl, (t) => {
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA16F, 2, 2);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
  });
